import type { TFunction } from 'i18next';
import type { CookieConsentConfig, Section } from 'vanilla-cookieconsent';
import type { ConsentableService, ConsentCategory } from './services';
import { notifyConsentChanged } from './consentState';

interface BuildInput {
  active: readonly ConsentableService[];
  revision: number;
  language: string;
  t: TFunction;
}

const CATEGORY_ORDER: readonly ConsentCategory[] = ['analytics', 'marketing'];

/**
 * Pure builder of the vanilla-cookieconsent config from the active services.
 * Exhaustively documents the choices the spec pins:
 *   - opt-in: nothing runs before consent;
 *   - cookie scoped to the shop's own hostname (the library forces
 *     Domain=location.hostname): shops share the *.foto2.me wildcard, and a
 *     .foto2.me parent domain would leak one shop's consent into every
 *     other shop;
 *   - 182 days: honour a refusal for six months (CNIL) before asking again;
 *   - bar at the bottom, equal-weight Accept all / Reject all: least obtrusive
 *     layout that still meets "reject as easy as accept";
 *   - categories: `necessary` (read-only) plus only the categories with an
 *     active service, each service togglable individually.
 */
export function buildConsentConfig({ active, revision, language, t }: BuildInput): CookieConsentConfig {
  const categories: CookieConsentConfig['categories'] = {
    necessary: { enabled: true, readOnly: true },
  };
  for (const cat of CATEGORY_ORDER) {
    const inCat = active.filter((s) => s.category === cat);
    if (inCat.length === 0) continue;
    categories[cat] = {
      autoClear: { cookies: inCat.flatMap((s) => [...(s.cookies ?? [])]) },
      services: Object.fromEntries(inCat.map((s) => [s.id, {
        label: t(`consent.services.${s.id}`),
        onAccept: () => { s.onAccept(); notifyConsentChanged(); },
        onReject: () => { s.onReject(); notifyConsentChanged(); },
      }])),
    };
  }

  const sections: Section[] = [
    { title: t('consent.preferences.intro_title'), description: t('consent.preferences.intro_description') },
    { title: t('consent.preferences.necessary_title'), description: t('consent.preferences.necessary_description'), linkedCategory: 'necessary' },
    ...CATEGORY_ORDER.filter((c) => c in categories).map((c) => ({
      title: t(`consent.preferences.${c}_title`),
      description: t(`consent.preferences.${c}_description`),
      linkedCategory: c,
    })),
    { title: t('consent.preferences.more_title'), description: t('consent.preferences.more_description', { url: '/cookies' }) },
  ];

  return {
    mode: 'opt-in',
    revision,
    cookie: { name: 'f2m_consent', domain: window.location.hostname, path: '/', sameSite: 'Lax', expiresAfterDays: 182 },
    guiOptions: {
      consentModal: { layout: 'bar inline', position: 'bottom center', equalWeightButtons: true, flipButtons: false },
      preferencesModal: { layout: 'box', equalWeightButtons: true, flipButtons: false },
    },
    categories,
    language: {
      default: language,
      translations: {
        [language]: {
          consentModal: {
            title: t('consent.modal.title'),
            description: t('consent.modal.description'),
            acceptAllBtn: t('consent.modal.accept_all'),
            acceptNecessaryBtn: t('consent.modal.reject_all'),
            showPreferencesBtn: t('consent.modal.manage'),
            footer: `<a href="/cookies">${t('consent.modal.footer_link')}</a>`,
          },
          preferencesModal: {
            title: t('consent.preferences.title'),
            acceptAllBtn: t('consent.modal.accept_all'),
            acceptNecessaryBtn: t('consent.modal.reject_all'),
            savePreferencesBtn: t('consent.preferences.save'),
            closeIconLabel: t('consent.preferences.close'),
            sections,
          },
        },
      },
    },
    onConsent: () => notifyConsentChanged(),
    onChange: () => notifyConsentChanged(),
  };
}
